import ProfileBookSection from '../book/ProfileBookSection';
import { profileBookSchema } from '../book/book-schema';
import { useEffect } from 'react';
import PageLayout from '../../components/base/PageLayout';
import HubCards from '../../components/feature/HubCards';

export default function About() {
  useEffect(() => {
    document.title = '執事ベルについて - 全日本執事協会頭取のプロフィール｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '全日本執事協会頭取・執事ベルの詳細プロフィール。西洋の伝統的な執事文化と日本のおもてなし精神を融合させた独自のスタイルを追求。執事文化の研究・発信、講演・研修の実施、おもてなしの心と完璧な技術の調和を目指す執事としての理念をご紹介します。');
    }

    // Add Keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '執事ベル, プロフィール, 全日本執事協会, 執事文化, おもてなし');
    } else {
      const keywordsTag = document.createElement('meta');
      keywordsTag.name = 'keywords';
      keywordsTag.content = '執事ベル, プロフィール, 全日本執事協会, 執事文化, おもてなし';
      document.head.appendChild(keywordsTag);
    }

    // Add Canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/about`);
    } else {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/about`;
      document.head.appendChild(canonical);
    }

    // Add Last-modified
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    } else {
      const lastModTag = document.createElement('meta');
      lastModTag.name = 'last-modified';
      lastModTag.content = new Date().toISOString().split('T')[0];
      document.head.appendChild(lastModTag);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.pageSchema = 'person';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      "mainEntity": {
        "@type": "Person",
        "@id": "https://butlerbell.jp/#person",
        "alternateName": ["執事ベル"],
        "sameAs": ["https://thebutler.jp/founder/", "https://butler-association.jp/related/butler-bell/", "https://butler-academy.jp/president"],
        "name": "執事ベル",
        "jobTitle": "全日本執事協会 頭取",
        "url": import.meta.env.VITE_SITE_URL || "https://butlerbell.jp",
        "description": "全日本執事協会頭取として、日本における執事文化の普及と発展に取り組んでいます。西洋の伝統的な執事文化と日本固有のおもてなし精神を融合させた、新しい執事像を追求しています。",
        "knowsAbout": ["執事文化", "おもてなし", "マナー", "ホスピタリティ", "VIP対応", "テーブルマナー"],
        "memberOf": {
          "@type": "Organization",
          "@id": "https://butler-association.jp/#organization",
          "url": "https://butler-association.jp/",
          "name": "全日本執事協会"
        }
      }
    });
    document.head.appendChild(script);
    const bookScript = document.createElement('script');
    bookScript.type = 'application/ld+json';
    bookScript.dataset.pageSchema = 'profile-book';
    bookScript.textContent = JSON.stringify(profileBookSchema);
    document.head.appendChild(bookScript);

    return () => {
      script.remove();
      bookScript.remove();
    };
  }, []);

  return (
    <PageLayout hero={<>
<section className="relative py-16 md:py-32 bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url('/images/gakken/gk-04.jpg')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 md:px-8 text-center">
          <h1 
            className="text-2xl md:text-8xl font-light text-white mb-4 md:mb-8 tracking-wide"
            style={{ fontFamily: '"Noto Serif JP", serif' }}
          >
            執事ベルについて
          </h1>
          <p 
            className="text-sm md:text-2xl text-amber-400 font-light tracking-wider"
            style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
          >
            全日本執事協会 頭取
          </p>
        </div>
      </section>
</>}>

      {/* Profile Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 md:px-8">
          <h2 
            className="text-xl md:text-5xl font-light text-[#1a1a1a] mb-8 md:mb-12 text-center"
            style={{ fontFamily: '"Noto Serif JP", serif' }}
          >
            プロフィール
          </h2>

          <div className="text-center mb-8 md:mb-12">
            <div className="relative inline-block">
              <div className="w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-[#917c50] shadow-2xl">
                <img 
                  src="/images/gakken/gk-14.jpg"
                  alt="執事ベル プロフィール写真"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center">
                <i className="ri-award-line text-white text-xl md:text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="space-y-3 md:space-y-6 text-gray-700 leading-8">
            <p className="text-base md:text-lg" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
              全日本執事協会頭取として、日本における執事文化の普及と発展に取り組んでいます。
            </p>
            
            <p className="text-base md:text-lg" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
              西洋の伝統的な執事文化と日本固有のおもてなし精神を融合させた、新しい執事像を追求しています。相手を思いやる心と洗練された所作を大切にし、執事文化の本質を探求し続けています。
            </p>
            
            <p className="text-base md:text-lg" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
              「真のおもてなしとは、相手を思いやる心と、完璧な技術の調和から生まれる」という信念のもと、執事文化の研究と実践を通じて日々学び続けている知見を、皆様と共有することを大切にしています。
            </p>

            <p className="text-base md:text-lg" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
              二〇二三年三月に頭取を拝命したとき、私が掲げたのは「執事は選ぶ時代へ」という一語でした。二代の頭取が守ってきたのは、執事は世に出ることなく黒子に徹し、確かなご紹介によってのみお客様とお会いするという流儀です。その矜持は長く協会の背骨でしたが、同時に、執事を必要とする方がご自身では出会えないという時代でもありました。在籍する執事の名簿を公開し、料金を明らかにし、ご紹介がなくともご依頼いただける窓口を設ける。長く閉じられていた扉を、内側から開ける決断でした。
            </p>

            <p className="text-base md:text-lg" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
              そして二〇二五年二月、一般社団法人全日本執事協会を設立し、代表理事に就任いたしました。この決断をめぐって、会の中で意見が分かれたことも事実です。長く協会を支えてきた方々は、これまでの流儀にこそ執事の本分があると考えました。議論は尽くし、それでも道を分かった者もおります。守ろうとしたものは、どちらの側も同じでした。去った方々が守ろうとした誇りもまた、協会が受け継いできたものです。その敬意を胸に、執事という仕事を次の百年へ渡すために、私はこの道を選びました。
            </p>
          </div>
        </div>
      </section>

      {/* Career History */}
      <section className="py-12 md:py-20 bg-[#f5f5f5]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 md:px-8">
          <h2 
            className="text-xl md:text-5xl font-light text-[#1a1a1a] text-center mb-8 md:mb-16"
            style={{ fontFamily: '"Noto Serif JP", serif' }}
          >
            活動内容
          </h2>
          
          <div className="space-y-6 md:space-y-12">
            <div className="bg-white rounded-lg p-6 md:p-8 shadow-lg">
              <div className="flex items-start space-x-4 md:space-x-6">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-white text-xl md:text-2xl"></i>
                </div>
                <div>
                  <h3 
                    className="text-base md:text-2xl font-medium text-[#1a1a1a] mb-2 md:mb-3"
                    style={{ fontFamily: '"Noto Serif JP", serif' }}
                  >
                    全日本執事協会 頭取
                  </h3>
                  <p 
                    className="text-sm md:text-base text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                  >
                    日本における執事文化の発展と普及に尽力。執事の技術向上と社会的地位の確立を目指し、様々な活動を展開しています。協会の創立は一八九〇年（明治二三年）、法人設立は二〇二五年二月一九日で、法人としての役職は代表理事にあたります。
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6 md:p-8 shadow-lg">
              <div className="flex items-start space-x-4 md:space-x-6">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-book-open-line text-white text-xl md:text-2xl"></i>
                </div>
                <div>
                  <h3 
                    className="text-base md:text-2xl font-medium text-[#1a1a1a] mb-2 md:mb-3"
                    style={{ fontFamily: '"Noto Serif JP", serif' }}
                  >
                    執事文化の研究・発信
                  </h3>
                  <p 
                    className="text-sm md:text-base text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                  >
                    西洋の執事文化と日本のおもてなしの心を融合させた独自のスタイルを追求しています。執事文化の本質を探求し、その知見を広く共有することを大切にしています。
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6 md:p-8 shadow-lg">
              <div className="flex items-start space-x-4 md:space-x-6">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-presentation-line text-white text-xl md:text-2xl"></i>
                </div>
                <div>
                  <h3 
                    className="text-base md:text-2xl font-medium text-[#1a1a1a] mb-2 md:mb-3"
                    style={{ fontFamily: '"Noto Serif JP", serif' }}
                  >
                    講演・研修の実施
                  </h3>
                  <p 
                    className="text-sm md:text-base text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                  >
                    企業や団体からのご依頼に応じて、おもてなしやマナーをテーマにした講演・研修を承っております。ご要望に合わせたプログラムをご提案いたします。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 md:px-8">
          <h2 
            className="text-xl md:text-5xl font-light text-[#1a1a1a] text-center mb-8 md:mb-16"
            style={{ fontFamily: '"Noto Serif JP", serif' }}
          >
            執事としての理念
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
            <div className="bg-gradient-to-br from-[#917c50]/10 to-[#b8975f]/5 rounded-lg p-6 md:p-8">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-heart-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 
                className="text-base md:text-2xl font-medium text-[#1a1a1a] mb-3 md:mb-4"
                style={{ fontFamily: '"Noto Serif JP", serif' }}
              >
                おもてなしの心
              </h3>
              <p 
                className="text-sm md:text-base text-gray-700 leading-relaxed"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                相手を思いやる心こそが、最高のサービスの源泉です。お客様の立場に立ち、何を求めているかを察知し、期待を超える体験をお届けすることを目指しています。
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-[#917c50]/10 to-[#b8975f]/5 rounded-lg p-6 md:p-8">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-star-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 
                className="text-base md:text-2xl font-medium text-[#1a1a1a] mb-3 md:mb-4"
                style={{ fontFamily: '"Noto Serif JP", serif' }}
              >
                完璧な技術
              </h3>
              <p 
                className="text-sm md:text-base text-gray-700 leading-relaxed"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                心だけでは真のサービスは成り立ちません。確かな技術と知識に裏打ちされた、プロフェッショナルとしての完璧な所作を追求し続けています。
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-[#917c50]/10 to-[#b8975f]/5 rounded-lg p-6 md:p-8">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-leaf-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 
                className="text-base md:text-2xl font-medium text-[#1a1a1a] mb-3 md:mb-4"
                style={{ fontFamily: '"Noto Serif JP", serif' }}
              >
                継続的な成長
              </h3>
              <p 
                className="text-sm md:text-base text-gray-700 leading-relaxed"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                執事としての道に終わりはありません。常に学び続け、自己研鑽を怠らず、より良いサービスを追求し続けることで、より高い価値をご提供できるよう努めています。
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-[#917c50]/10 to-[#b8975f]/5 rounded-lg p-6 md:p-8">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-global-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 
                className="text-base md:text-2xl font-medium text-[#1a1a1a] mb-3 md:mb-4"
                style={{ fontFamily: '"Noto Serif JP", serif' }}
              >
                文化の融合
              </h3>
              <p 
                className="text-sm md:text-base text-gray-700 leading-relaxed"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                西洋の執事文化と日本のおもてなしの心を融合させ、新しい価値を創造することを目指しています。伝統を大切にしながらも、現代のニーズに応える革新的なサービスを追求しています。
              </p>
            </div>
          </div>
        </div>
      </section>

      <ProfileBookSection />

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-[#917c50] to-[#b8975f]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 md:px-8 text-center">
          <h2 
            className="text-xl md:text-5xl font-light text-white mb-4 md:mb-8"
            style={{ fontFamily: '"Noto Serif JP", serif' }}
          >
            お気軽にお問い合わせください
          </h2>
          <p 
            className="text-sm md:text-xl text-white/90 mb-6 md:mb-12 leading-relaxed"
            style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
          >
            サービスに関するご質問、研修のご依頼など、<br />
            執事ベルが心を込めてご対応いたします。
          </p>
          <a 
            href="/contact"
            className="btn-gold inline-block bg-white text-[#917c50] px-6 md:px-12 py-3 md:py-4 rounded font-medium hover:bg-gray-100 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base"
            style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
          >
            お問い合わせフォームへ
          </a>
        </div>
      </section>
          <HubCards group="about" />
    </PageLayout>
  );
}
